import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import KybService from "../../../api/services/KybService";
import { toCanonicalStatus, unwrapSessionPayload } from "../../../utils/kycSessionPayload";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";

const TERMINAL_STATUSES = new Set(["APPROVED", "REJECTED"]);
const POLL_MS = 3000;

/** Map canonical KYB status → profile route (In Progress stays on pending; no separate route). */
export function resolveKybRouteForStatus(canonicalStatus) {
  switch (canonicalStatus) {
    case "APPROVED":
      return "/user_profile/kyb/success";
    case "REJECTED":
      return "/user_profile/kyb/failed";
    case "PENDING":
      return "/user_profile/kyb/pending";
    case "NOT_STARTED":
    case "EXPIRED":
    case "RESUBMISSION_REQUESTED":
      return "/user_profile/kyb";
    default:
      return null;
  }
}

export function useKybStatus({ enabled = true } = {}) {
  const [kybStatusRes, setKybStatusRes] = useState(null);
  const [hasResponse, setHasResponse] = useState(false);
  const lastStatusKeyRef = useRef(null);
  const onStatusChangeRef = useRef(null);

  const sessionPayload = useMemo(() => unwrapSessionPayload(kybStatusRes), [kybStatusRes]);
  const canonicalStatus = useMemo(
    () => toCanonicalStatus(sessionPayload?.status),
    [sessionPayload]
  );
  const diditStatus = sessionPayload?.diditStatus ?? sessionPayload?.didit_status ?? "";

  const loadKybStatus = useCallback(async () => {
    if (DISABLE_ALL_NETWORK || !enabled) return null;
    try {
      const res = await KybService.getStatus();
      const p = unwrapSessionPayload(res);
      const status = p?.status ?? res?.data?.status ?? null;
      const updatedAt = p?.updatedAt ?? p?.updated_at ?? null;
      const didit = p?.diditStatus ?? p?.didit_status ?? "";
      const key = `${status || ""}:${updatedAt || ""}:${didit || ""}`;
      const prevKey = lastStatusKeyRef.current;
      if (key && prevKey === key) return res;

      lastStatusKeyRef.current = key || null;
      setKybStatusRes(res);
      setHasResponse(true);

      if (key !== prevKey) {
        onStatusChangeRef.current?.({
          res,
          payload: p,
          canonicalStatus: toCanonicalStatus(status),
          diditStatus: didit,
        });
      }
      return res;
    } catch {
      setHasResponse(true);
      return null;
    }
  }, [enabled]);

  const setOnStatusChange = useCallback((fn) => {
    onStatusChangeRef.current = fn;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (DISABLE_ALL_NETWORK) {
      setHasResponse(true);
      return undefined;
    }
    void loadKybStatus();
    const st = canonicalStatus || "";
    if (TERMINAL_STATUSES.has(st)) return undefined;
    const id = window.setInterval(() => void loadKybStatus(), POLL_MS);
    return () => window.clearInterval(id);
  }, [loadKybStatus, canonicalStatus, enabled]);

  return {
    kybStatusRes,
    sessionPayload,
    canonicalStatus,
    diditStatus,
    hasResponse,
    loadKybStatus,
    setOnStatusChange,
  };
}

/** Fetch KYB status on mount, poll while non-terminal, and route to pending/success/failed views. */
export function useKybStatusRouting() {
  const navigate = useNavigate();
  const location = useLocation();
  const statusState = useKybStatus();
  const { setOnStatusChange, canonicalStatus, hasResponse, loadKybStatus } = statusState;

  useEffect(() => {
    setOnStatusChange(({ canonicalStatus: st }) => {
      const target = resolveKybRouteForStatus(st);
      if (target && location.pathname !== target) {
        navigate(target, { replace: true });
      }
    });
  }, [setOnStatusChange, navigate, location.pathname]);

  useEffect(() => {
    if (!hasResponse) return;
    const target = resolveKybRouteForStatus(canonicalStatus);
    if (target && location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [hasResponse, canonicalStatus, location.pathname, navigate]);

  useEffect(() => {
    const refresh = () => {
      void loadKybStatus();
    };
    window.addEventListener("didit:kyb:complete", refresh);
    window.addEventListener("didit:kyb:end", refresh);
    return () => {
      window.removeEventListener("didit:kyb:complete", refresh);
      window.removeEventListener("didit:kyb:end", refresh);
    };
  }, [loadKybStatus]);

  return statusState;
}
